export type FaqStakeholder = "buying" | "general" | "students" | "parents" | "schools" | "ngos" | "professionals";

export interface FaqItem {
  q: string;
  a: string;
  bullets?: string[];
  /** Closing paragraph rendered after the bullet list. */
  note?: string;

  /** Clickable citations — official sources behind the answer. */
  links?: Array<{ label: string; href: string; external?: boolean }>;
}

export interface FaqGroup {
  id: FaqStakeholder;
  label: string;
  description: string;
  items: FaqItem[];
  comingSoon?: boolean;
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: "buying",
    label: "Before you buy",
    description: "Straight answers before you decide — what WCC360\u2122 is, what you get, and why it isn\u2019t another test.",
    items: [
      {
        q: "Why are you building WeKIT\u2122?",
        a: "The world doesn\u2019t need another career test.\n\nIt needs a better way to help people discover their potential and navigate a future shaped by AI.\n\nWe believe every individual deserves more than a one-time report. They deserve lifelong Human Development Intelligence, meaningful mentoring relationships, real-world opportunities, and guidance that evolves as they do.\n\nThat\u2019s why we\u2019re building WeKIT\u2122\u2019s Human Development Intelligence Infrastructure for the AI Era \u2014 an ecosystem where Career Intelligence, mentoring, learning, and opportunity come together to help people build purposeful, future-ready lives.",
      },
      {
        q: "Is WCC360\u2122 just another psychometric or career assessment?",
        a: "No.\n\nMost career assessments give you a one-time report based on your responses.\n\nWCC360\u2122 is an AI-native Career Intelligence experience that helps you understand yourself, explore future opportunities, and continue growing as the world of work evolves.",
      },
      {
        q: "How is WCC360\u2122 different from ChatGPT, Claude, Gemini, or other AI tools?",
        a: "General AI assistants answer questions.\n\nWCC360\u2122 first understands you through a structured Career Intelligence assessment before providing personalized guidance.\n\nIt is also your entry point into WeKIT Mentoring\u2122, the mentoring and opportunity ecosystem within WeKIT\u2122\u2019s Human Development Intelligence Infrastructure for the AI Era.",
        note: "As the ecosystem grows, members will gain access to industry mentors, real-world projects, internships, career communities, employer opportunities, and lifelong Career Intelligence.",
      },
      {
        q: "Why should I pay when I can use free AI tools?",
        a: "Free AI tools provide general advice.\n\nWCC360\u2122 provides structured Career Intelligence built around your unique strengths, interests, motivations, aspirations, and future opportunities.\n\nYou\u2019re not paying for answers. You\u2019re investing in a personalized system designed to help you make better education and career decisions over time.",
      },
      {
        q: "What exactly do I get when I purchase WCC360\u2122 Pro?",
        a: "Your WCC360\u2122 Pro membership includes:",
        bullets: [
          "A comprehensive Career Intelligence assessment",
          "Your personalized Career Clarity Report",
          "Career DNA\u2122 profile",
          "AI-native Career Intelligence Dashboard",
          "Career exploration and pathway recommendations",
          "Five assessments over 12 months",
          "Continuous Career Intelligence updates",
          "AI guidance based on your evolving profile",
          "Access to WeKIT Mentoring\u2122 as new mentoring, learning, internship, and opportunity features are introduced",
        ],
      },
      {
        q: "How long does the assessment take?",
        a: "Most learners complete WCC360\u2122 in 20\u201330 minutes.\n\nYour personalized report and dashboard are available immediately after completion.",
      },
      {
        q: "Is this a one-time assessment?",
        a: "No.\n\nYour interests, strengths, confidence, skills, and aspirations evolve over time.\n\nWCC360\u2122 Pro includes multiple assessments throughout the year, helping your Career Intelligence stay current as you grow.",
      },
      {
        q: "Who is WCC360\u2122 designed for?",
        a: "WCC360\u2122 is designed for:",
        bullets: [
          "Students (13+)",
          "College students",
          "Young professionals",
          "Career changers",
          "Women re-entering careers",
          "Parents",
          "Schools and universities",
          "Anyone seeking greater clarity about their future",
        ],
      },
      {
        q: "Does WCC360\u2122 tell me what career I should choose?",
        a: "No.\n\nWCC360\u2122 doesn\u2019t make decisions for you.\n\nIt gives you the insights, evidence, and Career Intelligence you need to make confident, informed decisions that align with who you are.",
      },
      {
        q: "How many careers does WCC360\u2122 support?",
        a: "WCC360\u2122 currently maps learners to 5,000+ careers and career pathways across emerging and established industries.\n\nEach profile includes Career Intelligence such as future demand, required skills, AI impact, education pathways, salary insights, career progression, related occupations, and emerging opportunities.",
      },
      {
        q: "Is my data private and secure?",
        a: "Yes.\n\nYour information is securely stored and used only to generate your personalized Career Intelligence.\n\nYou control who can access your results, and WeKIT\u2122 never sells your personal assessment data.",
      },
      {
        q: "How many students have taken WCC360\u2122?",
        a: "WCC360\u2122 is a newly launched AI-native Career Intelligence platform, and we\u2019re excited to welcome our early community of learners.\n\nIt is built on the experience of WeKIT Mentoring\u2122, which has supported 5,000+ learners through mentoring, workshops, career guidance, and human development initiatives. As WCC360\u2122 grows, we plan to regularly share our community milestones and impact.",
      },
      {
        q: "What happens after I complete WCC360\u2122?",
        a: "Completing WCC360\u2122 is just the beginning.\n\nYour assessment becomes the foundation of your journey within WeKIT Mentoring\u2122, where you\u2019ll continue to receive Career Intelligence, guidance, and \u2014 over time \u2014 access to mentors, learning pathways, projects, internships, employer connections, and other opportunities designed to help you thrive in the AI era.",
      },
    ],
  },
  {
    id: "general",
    label: "General",
    description: "What WCC360 is and who it's for.",
    items: [
      {
        q: "What is WeKIT™ and what is WeKIT™ Career Clarity 360 (WCC360™)?",
        a: "WeKIT™ is an AI-native Human Development Intelligence Infrastructure for the AI Era, created to help students, young professionals, career changers, and lifelong learners discover their strengths, purpose, future-ready skills, and career pathways with confidence. At the heart of this infrastructure is WeKIT™ Career Clarity 360 (WCC360™), our AI-native Career Intelligence experience that goes beyond traditional career assessments. It combines structured human insights, Career Intelligence, and AI-native guidance to help individuals make informed decisions about their education, skills, and careers. WCC360™ is also your gateway to WeKIT Mentoring™, the mentoring and opportunity ecosystem within WeKIT's Human Development Intelligence Infrastructure. As the ecosystem grows, members will gain access to industry mentors, real-world projects, internships, career communities, learning pathways, and employer opportunities thus supporting lifelong growth in a rapidly changing world.",
        note: "Unlike traditional career assessments that provide a one-time report, WCC360™ is designed to evolve with you, delivering continuous Career Intelligence as you learn, grow, and navigate the future of work.",
      },

      {
        q: "How is WeKIT™ different from traditional psychometric tests?",
        a: "Traditional psychometric tests provide a one-time assessment and static report. WeKIT™ creates a Living Career Intelligence Platform that evolves with every learner through continuous assessments, reflections, skill development, career exploration, and AI-powered recommendations. Career guidance becomes an ongoing journey rather than a one-time event.",
      },
      {
        q: "What is Career Intelligence?",
        a: "Career Intelligence is the ability to combine personal strengths, interests, skills, aspirations, labour market data, future skills, and AI insights to help learners make informed career decisions. Instead of asking \"What career should I choose?\", WeKIT™ helps answer: \"Who am I becoming, and what opportunities best align with my unique potential?\"",
      },
      {
        q: "Where does WeKIT™ get its data?",
        a: "WeKIT™ combines multiple trusted sources of intelligence — Learner Intelligence collected directly through the Career Clarity 360™ Assessment (interests, strengths, skills, goals, reflections, learning progress, aspirations) and a Career Intelligence Engine informed by globally recognised occupational and skills frameworks:",
        bullets: [
          "NCO-2015 (National Classification of Occupations, India)",
          "ISCO-08 (International Standard Classification of Occupations, ILO)",
          "ESCO (European Skills, Competences, Qualifications and Occupations)",
          "O*NET Occupational Database",
          "National Skills Qualification Framework (NSQF)",
          "National Career Service (NCS)",
          "Industry Skills Councils",
          "Government labour market reports",
          "Emerging industry research",
        ],
        links: [
          {
            label: "NCO-2015 (Ministry of Labour & Employment, India)",
            href: "https://www.dget.nic.in/content/innerpage/national-classification-of-occupation.php",
            external: true,
          },
          {
            label: "ISCO-08 (International Labour Organization)",
            href: "https://ilostat.ilo.org/methods/concepts-and-definitions/classification-occupation/",
            external: true,
          },
        ],
      },
      {
        q: "How are the careers classified?",
        a: "Careers are classified against NCO-2015 and ISCO-08, the official Indian and international occupation standards. NCO-2015 is India's National Classification of Occupations, maintained by the Ministry of Labour & Employment; ISCO-08 is the International Labour Organization's global standard. Every one of the 5,877 roles in the registry carries its classification, which means:",
        bullets: [
          "A role means the same thing to a school, an employer, a university and a labour ministry",
          "Careers can be compared like for like, in India and abroad",
          "Official labour market data — demand, wages, skills — can be attached to the right role",
          "Nothing in the registry is a job title somebody invented for a report",
        ],
        links: [
          {
            label: "NCO-2015 (Ministry of Labour & Employment, India)",
            href: "https://www.dget.nic.in/content/innerpage/national-classification-of-occupation.php",
            external: true,
          },
          {
            label: "ISCO-08 (International Labour Organization)",
            href: "https://ilostat.ilo.org/methods/concepts-and-definitions/classification-occupation/",
            external: true,
          },
          { label: "How we apply these standards", href: "/occupation-standards" },
        ],
      },
      {
        q: "Does AI make the career decisions?",
        a: "No. AI does not choose careers for learners. Instead, AI analyzes learner strengths, interests, aspirations, skills, labour market trends, future skills, and education pathways to provide personalized recommendations that support informed decision-making. The learner — together with parents, teachers, mentors, and counsellors — remains at the centre of every decision.",
      },
      {
        q: "Is WeKIT™ replacing career counsellors?",
        a: "Not at all. WeKIT™ is designed to empower — not replace — career counsellors. By providing richer insights and real-time intelligence, WeKIT™ enables counsellors to have more meaningful and personalized conversations with learners.",
      },
      {
        q: "What makes WeKIT™ innovative?",
        a: "WeKIT™ introduces a completely new approach to career guidance, transforming it from a one-time assessment into a lifelong development experience through:",
        bullets: [
          "Continuous Career Intelligence™",
          "Living Career DNA™",
          "Purpose Intelligence™",
          "Future Command Center™",
          "365° Growth Journey™",
          "Opportunity Intelligence™",
        ],
      },
      {
        q: "What is Living Career DNA™?",
        a: "Living Career DNA™ is a dynamic learner profile that continuously evolves as learners grow, providing increasingly personalized guidance. It is built from:",
        bullets: [
          "Strengths",
          "Interests",
          "Skills",
          "Aspirations",
          "Goals",
          "Learning experiences",
          "Reflections",
          "AI-powered insights",
        ],
      },
      {
        q: "What are the seven Career DNA™ strands — and why aren't they all live?",
        a: "Career DNA™ is a seven-strand framework, and we show you exactly where each strand stands. Four strands read from your assessment today: Purpose DNA™, Strength DNA™, Learning DNA™ and Capability DNA™. Growth DNA™ is reserved on purpose — it measures trajectory, and trajectory is meaningless the first time you take the assessment, so it activates the second time you come back. Opportunity DNA™ and Contribution DNA™ are in build. You'll see the status on every strand, every time:",
        bullets: [
          "Purpose DNA™ — live",
          "Strength DNA™ — live",
          "Learning DNA™ — live",
          "Capability DNA™ — live",
          "Growth DNA™ — reserved; trajectory activates with your second assessment",
          "Opportunity DNA™ — in build",
          "Contribution DNA™ — in build",
        ],
      },
      {
        q: "What is the Future Command Center™?",
        a: "The Future Command Center™ is a personalized dashboard — a lifelong companion for career development — where learners can manage:",
        bullets: [
          "Career Intelligence",
          "Purpose",
          "Goals",
          "Future Skills",
          "Career Pathways",
          "Learning Plans",
          "Opportunities",
          "Progress",
          "Achievements",
        ],
      },
      {
        q: "How many careers does WCC360 support?",
        a: "WCC360\u2122 currently maps learners to 5,000+ careers and career pathways across emerging and established industries worldwide. Our AI-native Career Intelligence Engine continuously evolves to reflect the changing world of work, ensuring learners receive relevant, future-focused guidance. Each career profile includes rich Career Intelligence such as:",
        bullets: [
          "Future demand and employment outlook",
          "Required skills and competencies",
          "AI impact and automation readiness",
          "Education and learning pathways",
          "Salary and earning insights",
          "Career progression opportunities",
          "Related and adjacent career pathways",
          "Emerging roles and future occupations",
          "Industry trends and growth sectors",
          "Recommended certifications and future skills",
        ],
        note: "As new industries, technologies, and occupations emerge, the WCC360 Career Intelligence Engine is continuously updated, helping learners stay prepared for the careers of tomorrow, not just those of today.",
      },

      {
        q: "Who can use WeKIT™?",
        a: "WeKIT™ is designed for a broad range of learners and organisations:",
        bullets: [
          "Students",
          "Parents",
          "Schools",
          "Colleges",
          "Career Counsellors",
          "Educators",
          "NGOs",
          "Workforce Development Organisations",
          "Governments",
        ],
      },
      {
        q: "How does WeKIT™ help schools?",
        a: "Schools gain access to tools that enable data-driven decision-making and improved career guidance outcomes:",
        bullets: [
          "Student Career Intelligence Reports",
          "Principal Dashboards",
          "Future Skills Insights",
          "Career Trends",
          "Institutional Analytics",
          "Student Development Tracking",
        ],
      },
      {
        q: "How does WeKIT™ help parents?",
        a: "Parents receive personalized insights into their child's development journey — helping families make informed education and career decisions together:",
        bullets: [
          "Strengths",
          "Interests",
          "Learning preferences",
          "Career pathways",
          "Future skills",
          "Development journey",
        ],
      },
      {
        q: "Is student data secure?",
        a: "Yes. Protecting learner privacy is one of our highest priorities. Student information is used only to personalize each learner's experience and is handled in accordance with applicable privacy and data protection practices. WeKIT™ does not use individual student data to train public AI models.",
      },
      {
        q: "What is your vision?",
        a: "Our vision is to become the Human Development Infrastructure for the AI Era. We believe every learner deserves continuous guidance to discover purpose, develop future-ready capabilities, and confidently navigate the future of work.",
      },
      {
        q: "Why does WeKIT™ exist?",
        a: "We believe every learner has unique gifts, strengths, and potential. Our mission is to help every individual discover who they are, build the skills that matter, and connect with meaningful opportunities that enable them to thrive in a rapidly changing world. Rooting YOU in YOUR Purpose.",
      },
      {
        q: "What is WeKIT™ Career Clarity 360 (WCC360)?",
        a: "WCC360 is an AI-powered Future of Work Intelligence and Career Navigation Ecosystem. It blends psychometric principles, AI-powered career intelligence and future-readiness insights to help you understand who you are, what motivates you, how you learn, where you thrive, and which pathways align with your aspirations.",
      },
      {
        q: "What does the \"360\" in WCC360 stand for?",
        a: "It reflects a holistic view of you — strengths, interests, values, motivations, personality, learning preferences, lifestyle preferences, future aspirations, career opportunities and work environment fit — rather than a single score.",
      },
      {
        q: "How is this different from a traditional career test?",
        a: "Traditional tests output a label. WCC360 combines psychometrics, personality and strengths analysis, values and motivations, learning preferences, lifestyle fit, future-readiness indicators and exposure to 5,877 current and emerging roles — answering not just \"what should I become?\" but \"what kind of work and life will help me thrive?\"",
      },
      {
        q: "Is WCC360 only an assessment?",
        a: "No. The assessment is the starting point. WCC360 is part of the broader We-KIT ecosystem — mentor connections, personalised pathways, portfolio development, FutureReadySkills modules, goal setting, interview prep, scholarships, college matching and gamified learning.",
      },
      {
        q: "Is WCC360 available globally?",
        a: "Yes. It's designed to support learners and professionals across different countries and education systems.",
      },
      {
        q: "How long is my WCC360 psychometric test result valid for?",
        a: "Your WeKIT™ Career Clarity 360 results are valid for 12 months from the date of your purchase. During that year you can revisit your report any time, re-run sections, download updated PDFs, and get the benefit of ongoing improvements to our AI insights and career database.",
      },
      {
        q: "What happens after my 12-month results access ends?",
        a: "Once your 12 months are up, your private report dashboard, downloadable PDF, archetype radar, AI narrative, and career matches stop being accessible inside your account. Nothing is deleted — you can simply renew to unlock everything again. Specifically, after expiration:",
        bullets: [
          "Your dashboard and results page show an 'access expired' state instead of the report",
          "You can no longer download a new PDF or share a new public link",
          "Saved careers and your basic profile remain on your account",
          "You'll need to resubscribe (renew the assessment) to view your report again",
          "Lifetime membership is coming soon — when it launches you'll be able to switch to it instead of renewing each year",
        ],
      },
      {
        q: "Will I be reminded before my results expire?",
        a: "Yes. We'll automatically email you about 30 days before your 12-month access ends, so you have plenty of time to renew or wait for lifetime membership. The exact expiration date is also shown in your profile and at the top of your results page.",
      },
      {
        q: "Can I get lifetime access instead of renewing every year?",
        a: "Lifetime membership for WCC360 is coming soon. Until then, access is sold in 12-month windows. If you'd like to be notified the moment lifetime membership launches, join the waitlist on our homepage — anyone with an active subscription at launch will get a preferential upgrade path.",
      },
    ],
  },
  {
    id: "students",
    label: "Students",
    description: "Taking the assessment and making sense of your results.",
    items: [
      {
        q: "How long does the assessment take?",
        a: "Most users complete the assessment in 30 to 60 minutes, depending on the version and level of depth selected. Answer thoughtfully and honestly to obtain the most meaningful insights.",
      },
      {
        q: "Do I need to prepare before taking the assessment?",
        a: "No special preparation is required. WCC360 is not an examination and there are no right or wrong answers. The most valuable results come from answering honestly and reflecting your genuine preferences and experiences.",
      },
      {
        q: "Will WCC360 tell me exactly which career to choose?",
        a: "No. WCC360 does not make decisions on your behalf. It provides guidance and recommendations based on your profile and encourages exploration of multiple pathways. Career decisions should be informed by personal aspirations, experiences, discussions with trusted adults and ongoing learning.",
      },
      {
        q: "Can I take the assessment more than once?",
        a: "Yes. Interests, goals and experiences evolve over time. You may retake the assessment periodically to gain updated insights and reflect changes in your aspirations and preferences.",
      },
      {
        q: "What if I have multiple interests?",
        a: "Having multiple interests is completely normal. WCC360 identifies clusters of strengths and career pathways rather than restricting you to a single profession. Many careers today are interdisciplinary, and diverse interests can be an advantage in an increasingly interconnected world.",
      },
      {
        q: "What if I do not know what I want to become?",
        a: "Many students are uncertain about their future, and this is entirely normal. WCC360 is designed to support self-discovery and exploration rather than expecting you to have all the answers. Its purpose is to help you better understand yourself and gradually build confidence and clarity over time.",
      },
      {
        q: "Can WCC360 help me choose subjects or streams?",
        a: "Yes. WCC360 provides insights that can support decisions regarding subject combinations and educational pathways. However, it does not prescribe a single route and should be used alongside discussions with parents, teachers, counsellors and mentors.",
      },
      {
        q: "Does WCC360 include future careers?",
        a: "Yes. WCC360 provides exposure to 5,877 current and emerging career roles, including opportunities expected to evolve as technology and society continue to change. The system helps you prepare for the future of work rather than focusing only on traditional professions.",
      },
      {
        q: "Will WCC360 help me understand what careers are actually like?",
        a: "Yes. Beyond recommending careers, WCC360 provides deeper insights into the realities of different professions. Future capabilities are expected to include:",
        bullets: [
          "Indoor versus outdoor work environments",
          "Movement and activity levels",
          "Public speaking requirements",
          "Interaction with people",
          "Travel intensity",
          "Screen time expectations",
          "Schedule flexibility",
          "Sensory exposure",
          "Physical work requirements",
          "Typical workplace environments",
        ],
      },
      {
        q: "Will WCC360 consider my personality and values?",
        a: "Yes. WCC360 seeks to understand not only what you may enjoy doing but also what matters to you. Factors considered include:",
        bullets: [
          "Creativity",
          "Stability",
          "Recognition",
          "Autonomy",
          "Income aspirations",
          "Desire for impact",
          "Teamwork preferences",
          "Need for variety or deep focus",
        ],
      },
      {
        q: "Can WCC360 help me prepare for the future of work?",
        a: "Yes. WCC360 is designed with future readiness in mind. Beyond career exploration, the broader We-KIT ecosystem aims to support:",
        bullets: [
          "Life skills",
          "Goal setting",
          "Journaling",
          "Interview preparation",
          "Personalised learning",
          "Mentor connections",
          "Skill development",
          "FutureReadySkills microlearning",
        ],
      },
      {
        q: "Will I discover careers I have never heard of before?",
        a: "Yes. Many users are surprised by the diversity of opportunities available today. WCC360 expands awareness and exposes you to a broad range of professions across industries, including emerging roles influenced by advances in technology, sustainability, healthcare and entrepreneurship.",
      },
      {
        q: "Can I explore more than one career pathway?",
        a: "Yes. WCC360 recognises that there are many ways to build a meaningful and fulfilling life. Rather than focusing on a single answer, the system encourages exploration and presents multiple pathways that align with your strengths, interests and aspirations.",
      },
      {
        q: "Will WCC360 provide mentor connections?",
        a: "Yes. Future versions are expected to connect learners with mentors and professionals aligned with their areas of interest. These interactions may help students:",
        bullets: [
          "Explore careers more deeply",
          "Learn from practitioners",
          "Understand industry expectations",
          "Build confidence",
          "Expand their networks",
          "Develop professional skills",
        ],
      },
      {
        q: "Will WCC360 help build future-ready skills?",
        a: "Yes. WCC360 is part of the broader We-KIT ecosystem, which aims to support lifelong growth through:",
        bullets: [
          "Goal setting",
          "Reflection and journaling",
          "Habit building",
          "Communication skills",
          "Leadership skills",
          "Critical thinking",
          "AI literacy",
          "Entrepreneurship",
          "Emotional intelligence",
          "Interview preparation",
          "Portfolio development",
        ],
      },
      {
        q: "Will WCC360 help with college applications?",
        a: "Yes. Future capabilities are intended to help learners create meaningful pathways aligned with their aspirations, so students can build compelling profiles that reflect their interests, strengths and aspirations. Support may include:",
        bullets: [
          "College assessment and matching",
          "Pathway recommendations",
          "Course suggestions",
          "Skill gap identification",
          "Extracurricular recommendations",
          "Project ideas",
          "Portfolio development",
          "Career-aligned experiences",
          "Scholarship opportunities",
        ],
      },
      {
        q: "Will my WCC360 access expire?",
        a: "WCC360 is a 12-month Career Growth Journey — your annual subscription includes 5 career check-ins (at sign-up, 3, 6, 9, and 12 months) and continuous access to your Career Intelligence Dashboard. During the year you benefit from ongoing updates as careers, industries, and aspirations evolve — guidance is a continuous journey, not a one-time event. Updates may include:",
        bullets: [
          "Ongoing updates",
          "New career information",
          "Emerging role additions",
          "Enhanced reports",
          "Evolving AI insights",
          "Future ecosystem capabilities as they become available",
        ],
      },
      {
        q: "Can WCC360 help me build a pathway rather than just recommend careers?",
        a: "Yes. WCC360 aims to move beyond career recommendations and help users create personalized pathways structured around seven guiding questions:",
        bullets: [
          "Who am I? — strengths, interests, values and motivations",
          "Where could I go? — career possibilities and future opportunities",
          "What should I learn? — skills, courses, certifications and experiences",
          "Who can help me? — mentors and communities",
          "How can I demonstrate my growth? — projects, portfolios and credentials",
          "Where can I study? — college and program recommendations",
          "How can I stand out? — a compelling narrative for applications",
        ],
      },
      {
        q: "Why does WCC360 focus on the future of work?",
        a: "The world of work is changing rapidly. Advances in AI, automation, robotics, biotechnology, sustainability and frontier technologies are transforming industries and creating new opportunities, making adaptation and lifelong learning more important than ever. WCC360 was created to help individuals prepare not only for today's jobs, but for the opportunities and challenges of the decades ahead — moving beyond \"What should I become?\" to \"How can I thrive, adapt and contribute meaningfully in a rapidly changing world?\"",
      },
    ],
  },
  {
    id: "parents",
    label: "Parents",
    description: "Supporting your child's exploration, choices and confidence.",
    items: [
      {
        q: "How can WCC360 help parents?",
        a: "WCC360 helps you gain a deeper understanding of your child's strengths, preferences and future possibilities, enabling more informed and meaningful conversations about education and career pathways. Specifically it helps you:",
        bullets: [
          "Understand your child's strengths, interests, values, personality and learning preferences",
          "Encourage informed and constructive conversations around subject and career choices",
          "Reduce confusion and anxiety surrounding future decisions",
          "Support long-term growth, well-being and future readiness",
          "Explore multiple pathways rather than focusing on a single profession",
          "Discover careers aligned with your child's unique profile and aspirations",
        ],
      },
      {
        q: "What kind of career exposure does my child get?",
        a: "WCC360 provides insights into 5,877 current and emerging career roles across industries — from AI, Robotics and Space to Climate Tech, HealthTech, FinTech, AgriTech and the creative economy.",
      },
      {
        q: "Will we understand what careers are really like day-to-day?",
        a: "Yes. Future capabilities are expected to help families understand the lived reality of careers, including:",
        bullets: [
          "Indoor versus outdoor work",
          "Movement levels",
          "Public speaking requirements",
          "People interaction",
          "Travel intensity",
          "Screen time expectations",
          "Work schedules",
          "Environment and sensory exposure",
          "Physical work requirements",
        ],
      },
      {
        q: "Does WCC360 consider lifestyle and work preferences?",
        a: "Yes. WCC360 looks beyond profession titles to understand what kind of life and work environment your child will thrive in, including:",
        bullets: [
          "Desire for autonomy",
          "Need to create impact",
          "Income aspirations",
          "Preference for prestige",
          "Stability and predictability",
          "Creativity and self-expression",
          "Focus versus variety",
          "Solo versus team preferences",
          "Social demands",
          "Emotional demands",
        ],
      },
      {
        q: "What's coming next in the We-KIT ecosystem?",
        a: "Planned capabilities include:",
        bullets: [
          "Mentor connections",
          "\"Day in the Life\" career experiences",
          "Goal setting and journaling",
          "Gamified life skills",
          "Interview preparation",
          "College assessment and matching",
          "Scholarship opportunities",
          "Personalised learning pathways",
          "Internship pathways",
          "FutureReadySkills modules",
        ],
      },
      {
        q: "What if my child is interested in multiple careers?",
        a: "This is completely normal. WCC360 helps identify multiple pathways and encourages exploration rather than limiting students to a single profession.",
      },
      {
        q: "Are the careers shown real, recognised occupations?",
        a: "Yes. Careers are classified against NCO-2015 and ISCO-08, the official Indian and international occupation standards, so every role your child sees is a recognised occupation rather than a made-up job title. That also means the pathway, subject and skill guidance attached to it lines up with how schools, universities and employers describe the same work.",
        links: [
          {
            label: "NCO-2015 (Ministry of Labour & Employment, India)",
            href: "https://www.dget.nic.in/content/innerpage/national-classification-of-occupation.php",
            external: true,
          },
          {
            label: "ISCO-08 (International Labour Organization)",
            href: "https://ilostat.ilo.org/methods/concepts-and-definitions/classification-occupation/",
            external: true,
          },
          { label: "How we apply these standards", href: "/occupation-standards" },
        ],
      },
      {
        q: "Can WCC360 help if my child is confused about subject choices?",
        a: "Yes. WCC360 can provide insights that support informed decisions regarding subjects and educational pathways while complementing discussions with educators and counsellors.",
      },
      {
        q: "Will WCC360 tell my child which stream to choose?",
        a: "No. WCC360 does not prescribe a single stream or academic pathway. It provides guidance and highlights multiple possibilities that may align with the student's profile.",
      },
      {
        q: "Does WCC360 account for changing interests and aspirations?",
        a: "Yes. Career development is a journey, and interests evolve over time. Students may revisit the assessment periodically to gain fresh insights as they grow and develop.",
      },
      {
        q: "Can WCC360 help reduce career-related stress and uncertainty?",
        a: "Yes. By providing structured insights and presenting multiple possibilities, WCC360 brings greater clarity and confidence to career-related conversations at home.",
      },
      {
        q: "Can parents and children review the report together?",
        a: "Yes. WCC360 is designed to encourage meaningful conversations between students and their families. Reviewing the report together can strengthen understanding and support informed decision-making.",
      },
      {
        q: "Does WCC360 replace career counsellors or parental guidance?",
        a: "No. WCC360 is intended to complement — not replace — the support provided by parents, teachers, counsellors and mentors. Human relationships and conversations remain essential.",
      },
      {
        q: "How early should career exploration begin?",
        a: "Career awareness can begin early, but deeper exploration is generally most valuable from Grades 8–12, when students begin making important educational decisions.",
      },
      {
        q: "My child is unsure about the future. Can WCC360 still help?",
        a: "Yes. Many students experience uncertainty, and this is entirely normal. WCC360 is designed to support exploration, self-awareness and confidence-building rather than expecting students to have all the answers.",
      },
    ],
  },
  {
    id: "schools",
    label: "Schools",
    description: "Implementing WCC360 across grades, curricula and counseling programs.",
    items: [
      {
        q: "Can schools implement WCC360 for all students?",
        a: "Yes. WCC360 is designed to be implemented across entire grades, departments, or selected student cohorts. Schools may deploy the program as part of their career guidance, future readiness, life skills, or counseling initiatives.",
      },
      {
        q: "Which age groups are best suited for WCC360?",
        a: "WCC360 is particularly designed for students in Grades 8–12, when they begin making important decisions regarding subjects, higher education, and future pathways.",
      },
      {
        q: "Does WCC360 align with the National Education Policy (NEP) 2020?",
        a: "Yes. WCC360 supports many of the objectives of NEP 2020, including:",
        bullets: [
          "Holistic development",
          "Personalized learning",
          "Career awareness",
          "Experiential learning",
          "Future readiness",
          "Life skills development",
          "Informed decision-making",
        ],
      },
      {
        q: "Is WCC360 suitable for international schools?",
        a: "Yes. WCC360 is designed to support learners across different curricula, including:",
        bullets: [
          "CBSE",
          "ICSE",
          "State Boards",
          "IB",
          "IGCSE",
          "American Curriculum",
          "Other international systems",
        ],
      },
      {
        q: "Does WCC360 replace school counselors?",
        a: "No. WCC360 is intended to complement — not replace — the work of school counselors, educators, and mentors. Human guidance and meaningful conversations remain essential.",
      },
      {
        q: "Can schools receive institutional reports?",
        a: "Yes. Schools may receive aggregated insights that help identify:",
        bullets: [
          "Student interests",
          "Emerging career trends",
          "Skill gaps",
          "Aspirations",
          "Program effectiveness",
        ],
      },
      {
        q: "Can schools customize the program?",
        a: "Yes. Implementations may include:",
        bullets: [
          "Career awareness sessions",
          "Parent orientations",
          "Teacher workshops",
          "FutureReadySkills modules",
          "Mentorship experiences",
          "Panel discussions",
          "Industry interactions",
        ],
      },
      {
        q: "Can WCC360 help students with subject and stream selection?",
        a: "Yes. WCC360 provides insights that can support decisions related to subjects and pathways while encouraging exploration and informed discussions.",
      },
      {
        q: "Does WCC360 focus on emerging careers?",
        a: "Yes. Students gain exposure to 5,877 current and emerging careers across sectors including:",
        bullets: [
          "Artificial Intelligence",
          "Robotics",
          "Space Economy",
          "Electric Vehicles",
          "Climate Technology",
          "Biotechnology",
          "Cybersecurity",
          "Renewable Energy",
          "HealthTech",
          "FinTech",
          "Drone Technologies",
          "Smart Manufacturing",
        ],
      },
      {
        q: "Is career information updated regularly?",
        a: "Yes. WCC360 is designed as a living ecosystem. Career information, industry trends, and emerging opportunities are continuously refreshed to reflect the changing world of work.",
      },
      {
        q: "Can schools use WCC360 as part of their career guidance program?",
        a: "Yes. WCC360 can be integrated into:",
        bullets: [
          "Counseling programs",
          "Life skills initiatives",
          "Career guidance frameworks",
          "Advisory periods",
          "Future readiness programs",
        ],
      },
      {
        q: "Does WCC360 help students prepare for college admissions?",
        a: "Yes. Future capabilities are intended to support:",
        bullets: [
          "Portfolio development",
          "Skill gap identification",
          "Project recommendations",
          "Extracurricular planning",
          "College matching",
          "Scholarship opportunities",
        ],
      },
      {
        q: "Can WCC360 support project-based and experiential learning?",
        a: "Yes. WCC360 aligns with experiential learning approaches and encourages students to connect their interests with meaningful projects and real-world experiences.",
      },
      {
        q: "Will mentor connections be available?",
        a: "Future capabilities are expected to connect students with mentors and professionals aligned with their interests and aspirations.",
      },
      {
        q: "How long does student access remain valid?",
        a: "WCC360 is a 12-month Career Growth Journey. An annual subscription includes 5 assessments across the year (sign-up, 3, 6, 9, and 12 months) and continuous dashboard access. During this period, learners benefit from:",
        bullets: [
          "Updated reports",
          "New career additions",
          "Enhanced insights",
          "Evolving features",
          "Continuous learning pathways",
        ],
      },
      {
        q: "Can schools purchase licenses in bulk?",
        a: "Yes. Customized institutional packages are available for schools seeking to support larger groups of learners.",
      },
      {
        q: "Can WCC360 help schools prepare students for the future of work?",
        a: "Yes. WCC360 was designed to prepare learners for an AI-enabled world by helping them develop self-awareness, adaptability, and future-ready skills.",
      },
      {
        q: "Why should schools adopt WCC360?",
        a: "Because the world has changed. Career guidance must evolve beyond traditional professions and static assessments. WCC360 helps schools prepare students not only for examinations, but for life, work, and purpose in an increasingly dynamic world.",
      },
    ],
  },
  {
    id: "ngos",
    label: "NGOs & Foundations",
    description: "Reaching underserved youth with future-of-work intelligence and opportunity.",
    items: [
      {
        q: "Can NGOs use WCC360?",
        a: "Yes. WCC360 is designed to support nonprofit organizations, foundations, community groups, and social impact initiatives working with youth.",
      },
      {
        q: "Which beneficiaries can benefit from WCC360?",
        a: "WCC360 can support:",
        bullets: [
          "School students",
          "College students",
          "Rural youth",
          "Girls and young women",
          "Underprivileged communities",
          "First-generation learners",
          "Youth seeking career guidance",
          "Employability and livelihood programs",
        ],
      },
      {
        q: "Does WeKIT™ offer special pricing for NGOs?",
        a: "Yes. Registered NGOs and charitable organizations are eligible for a 50% discount. Organizations primarily serving girls and young women are eligible for an additional 50% discount on the already discounted price, resulting in an effective discount of 75%.",
      },
      {
        q: "Why does WeKIT™ offer discounted pricing for NGOs?",
        a: "We believe that financial circumstances should never prevent young people from accessing opportunities and preparing for the future. Our mission is to make future-of-work intelligence accessible to underserved communities.",
      },
      {
        q: "Can WCC360 support girls' empowerment initiatives?",
        a: "Yes. WCC360 is particularly well suited for programs focused on:",
        bullets: [
          "Girls' education",
          "Career awareness",
          "Employability",
          "Leadership development",
          "STEM participation",
          "Confidence building",
          "Future readiness",
        ],
      },
      {
        q: "Can NGOs implement WCC360 at scale?",
        a: "Yes. WCC360 can support initiatives ranging from small community programs to large-scale social impact projects involving thousands of learners.",
      },
      {
        q: "Can WCC360 support livelihood and employability programs?",
        a: "Yes. WCC360 helps young people understand their strengths, explore opportunities, and prepare for future careers and skills.",
      },
      {
        q: "Does WCC360 focus only on traditional careers?",
        a: "No. Learners are exposed to 5,877 current and emerging career roles, including opportunities in AI, Space Economy, EVs, ClimateTech, Robotics, Biotechnology, and other frontier industries.",
      },
      {
        q: "Is the career information updated continuously?",
        a: "Yes. Unlike static career books, WCC360 is designed as a living ecosystem with continuously evolving career intelligence and industry insights.",
      },
      {
        q: "Can NGOs measure impact?",
        a: "Yes. Institutional reports may help organizations understand:",
        bullets: [
          "Aspirations of beneficiaries",
          "Career interests",
          "Emerging trends",
          "Engagement levels",
          "Program outcomes",
        ],
      },
      {
        q: "Can NGOs receive bulk pricing?",
        a: "Yes. Special institutional pricing and customized implementations are available depending on the scale and nature of the initiative.",
      },
      {
        q: "Can CSR programs and foundations partner with WeKIT™?",
        a: "Yes. We welcome partnerships with:",
        bullets: [
          "Corporate CSR initiatives",
          "Foundations",
          "Philanthropic organizations",
          "Educational nonprofits",
          "Youth development programs",
        ],
      },
      {
        q: "Can sponsors fund access for deserving students?",
        a: "Yes. Sponsors and donors may support individual learners or larger groups through scholarship and sponsorship programs.",
      },
      {
        q: "Does WCC360 help students prepare for college and scholarships?",
        a: "Future capabilities are intended to support:",
        bullets: [
          "Portfolio development",
          "Project recommendations",
          "Extracurricular planning",
          "Scholarship discovery",
          "College exploration",
          "College matching",
        ],
      },
      {
        q: "Will mentor connections and future-ready learning be available?",
        a: "Yes. Future versions of WCC360 are expected to include:",
        bullets: [
          "Mentor matching",
          "FutureReadySkills modules",
          "Goal setting",
          "Journaling",
          "Gamified learning experiences",
          "Interview preparation",
          "Lifelong learning pathways",
        ],
      },
      {
        q: "Why should NGOs adopt WCC360?",
        a: "Because the future of work is changing rapidly, and underserved youth deserve access to the same quality of career intelligence and opportunities available to everyone else. WCC360 seeks to ensure that talent is not limited by geography, gender, or financial circumstances. Rooting Young People in Their Purpose — No Child Left Behind in the Age of AI.",
      },
    ],
  },
  {
    id: "professionals",
    label: "Professionals",
    description: "Career changers, mid-career growth, entrepreneurs and lifelong learners.",
    items: [
      {
        q: "Is WCC360 only for students?",
        a: "No. While WCC360 is particularly valuable for students and young people, it is also designed for professionals at different stages of their careers, including early-career individuals, mid-career professionals, career changers, returning professionals, entrepreneurs, and lifelong learners.",
      },
      {
        q: "What can someone with 5 years of experience expect from the programme?",
        a: "WCC360 is designed for both students and working professionals.\n\nWith five years behind you, the most useful question usually isn\u2019t \u201cwhat job should I do?\u201d \u2014 it\u2019s whether you\u2019re on the right trajectory, and what your next move should be.",
        bullets: [
          "Understand your natural strengths, work style, motivations and decision-making patterns through psychometric analysis.",
          "Discover career paths and adjacent roles that fit your profile more closely, including emerging AI-era careers you may not have considered.",
          "See where your skill gaps are and build a personalised development roadmap instead of following generic advice.",
          "Get clarity on whether to deepen your current specialisation, move into leadership, switch domains, or explore entrepreneurship.",
        ],
        note: "Many assessments stop at a personality type. WCC360 connects your profile to 5,000+ career pathways, explains what those careers are actually like day to day (including the downsides), and gives AI-guided recommendations based on your own results. Professionals use it during promotions, transitions, burnout, and when they\u2019re simply asking, \u201cis this what I want to be doing for the next ten years?\u201d",
      },
      {
        q: "Can WCC360 help with career transitions?",
        a: "Yes. WCC360 is designed to support individuals exploring:",
        bullets: [
          "Career changes",
          "New industries",
          "Leadership pathways",
          "Entrepreneurship",
          "Portfolio careers",
          "Purpose-driven work",
          "Emerging opportunities",
        ],
      },
      {
        q: "Is WCC360 suitable for mid-career professionals?",
        a: "Yes. Career development is a lifelong journey. Many professionals revisit their aspirations and priorities throughout their careers, and WCC360 seeks to support this process through self-awareness, future-of-work intelligence, and personalized pathways.",
      },
      {
        q: "Can WCC360 help me rediscover my purpose?",
        a: "Yes. Many professionals reach points where they seek greater meaning, fulfillment, or alignment between their work and values. WCC360 is designed to help individuals understand their strengths, motivations, and aspirations, and explore pathways that align with both professional success and personal well-being.",
      },
      {
        q: "Can WCC360 help me identify my strengths?",
        a: "Yes. WCC360 explores multiple dimensions, including:",
        bullets: [
          "Interests",
          "Personality",
          "Values",
          "Motivations",
          "Work preferences",
          "Lifestyle considerations",
          "Desired impact",
          "Future aspirations",
        ],
      },
      {
        q: "Can WCC360 help me explore emerging careers?",
        a: "Yes. WCC360 provides exposure to 5,877 current and emerging career roles. Examples include opportunities in:",
        bullets: [
          "Artificial Intelligence",
          "Human-AI Collaboration",
          "Climate Technology",
          "Electric Vehicles",
          "Robotics",
          "Biotechnology",
          "Space Economy",
          "Cybersecurity",
          "Renewable Energy",
          "Quantum Computing",
          "Digital Health",
          "Creator Economy",
          "Entrepreneurship",
        ],
      },
      {
        q: "Is the career information updated regularly?",
        a: "Yes. WCC360 is designed as a living ecosystem. Career intelligence, industry trends, and emerging opportunities are intended to be refreshed continuously using evolving and real-time data sources. This helps ensure that users remain informed about changing opportunities and skill requirements.",
      },
      {
        q: "Can WCC360 help professionals prepare for the future of work?",
        a: "Yes. Rapid advances in AI, automation, and frontier technologies are transforming industries and creating entirely new categories of work. WCC360 seeks to help professionals:",
        bullets: [
          "Understand changing trends",
          "Identify growth opportunities",
          "Develop future-ready skills",
          "Build resilience and adaptability",
          "Navigate lifelong learning",
        ],
      },
      {
        q: "Can WCC360 help professionals understand work-life alignment?",
        a: "Yes. Career satisfaction is influenced by more than income and titles. WCC360 considers lifestyle preferences (indoor vs outdoor, movement, travel, screen time, schedule flexibility), work preferences (team vs solo, public speaking, human interaction, stability vs variety) and personal motivations such as:",
        bullets: [
          "Autonomy",
          "Creativity",
          "Impact",
          "Recognition",
          "Income potential",
          "Security and predictability",
        ],
      },
      {
        q: "Can WCC360 help me understand whether I should remain in my current field or explore new ones?",
        a: "Yes. WCC360 encourages exploration rather than prescribing a single answer. It helps professionals understand transferable strengths and discover opportunities that may align with their evolving interests and aspirations.",
      },
      {
        q: "Can WCC360 support entrepreneurs and founders?",
        a: "Yes. Entrepreneurs and founders often wear multiple hats and operate in uncertain environments. WCC360 can help individuals better understand their strengths, motivations, leadership preferences, and areas for growth.",
      },
      {
        q: "Can WCC360 help returning professionals?",
        a: "Yes. Professionals returning after career breaks or other life transitions may use WCC360 to rediscover strengths, identify opportunities, and build confidence for their next chapter.",
      },
      {
        q: "Will WCC360 help me develop future-ready skills?",
        a: "Yes. The broader We-KIT ecosystem is designed to support:",
        bullets: [
          "AI literacy",
          "Leadership",
          "Communication",
          "Critical thinking",
          "Emotional intelligence",
          "Entrepreneurship",
          "Adaptability",
          "Lifelong learning",
        ],
      },
      {
        q: "Will mentor connections be available?",
        a: "Future capabilities are expected to enable connections with mentors and professionals aligned with users' interests and aspirations. These interactions may help individuals gain insights, expand networks, and accelerate growth.",
      },
      {
        q: "Can WCC360 help with portfolio development?",
        a: "Yes. Future capabilities are intended to support:",
        bullets: [
          "Project recommendations",
          "Portfolio development",
          "Certifications",
          "Learning pathways",
          "Personal branding",
          "Skill gap identification",
        ],
      },
      {
        q: "Can WCC360 help with career storytelling and personal branding?",
        a: "Future capabilities are expected to help users articulate their experiences, strengths, aspirations, and unique value propositions more effectively. These capabilities may support networking, interviews, and professional development.",
      },
      {
        q: "How long does access remain valid?",
        a: "WCC360 is a 12-month Career Growth Journey. An annual subscription includes 5 assessments over the year (sign-up, 3, 6, 9, and 12 months) plus continuous dashboard access. During this period, users benefit from:",
        bullets: [
          "Updated reports",
          "New career additions",
          "Enhanced recommendations",
          "Evolving features",
          "Ongoing insights",
        ],
      },
      {
        q: "Is WCC360 a replacement for coaches or mentors?",
        a: "No. WCC360 is intended to complement — not replace — the support provided by coaches, mentors, managers, and trusted advisors. Human relationships remain essential to growth and development.",
      },
      {
        q: "What is the long-term vision for WCC360?",
        a: "WCC360 aims to become a lifelong Future of Work Intelligence and Career Navigation Ecosystem. Its mission extends beyond helping individuals answer \"What job should I pursue?\" to helping them answer \"Who am I becoming, and how can I continue to grow, adapt, and contribute meaningfully in a changing world?\"",
      },
      {
        q: "Why should professionals use WCC360?",
        a: "Because careers are no longer linear. Industries are changing. AI is transforming work. New opportunities are emerging. And perhaps the greatest career advantage of the future will not be knowing all the answers — but knowing yourself. WCC360 seeks to help individuals navigate uncertainty with greater clarity, confidence, and purpose. Rooting You In Your Purpose — Preparing Humans for the Age of AI.",
      },
    ],
  },
];
